import { useAuth } from '@clerk/clerk-react';
import {
  createContext,
  createElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

// Active-tenant context for the dashboard SPA (Task 25 / MIS-806).
//
// The dashboard is multi-tenant: a Clerk user can belong to several tenants
// (their personal tenant plus any organizations). Exactly one is "active" at a
// time, and every authenticated API call carries it as the `X-Tenant-Id` header
// (see lib/api.ts) so the worker's tenantMiddleware can resolve + membership-
// verify `c.var.tenantId`.
//
// Source of truth for the selection is localStorage (key
// `lander_mgr_active_tenant_id`) so a page refresh preserves it; React state is
// only a mirror. On mount the provider also fetches `/api/me` to learn which
// tenants the user actually has, and — if they have exactly one — pre-populates
// `defaultTenantId` (and adopts it as active when nothing is stored yet).

/** localStorage key holding the active tenant id. Exact string — no variants. */
export const ACTIVE_TENANT_STORAGE_KEY = 'lander_mgr_active_tenant_id';

export interface TenantContextValue {
  /** The currently selected tenant, or null if none chosen / user has none. */
  activeTenantId: string | null;
  /** Persist + switch the active tenant. Pass a tenant the user can access. */
  setActiveTenantId: (tenantId: string) => void;
  /** The user's single tenant, if `/api/me` reports exactly one; else null. */
  defaultTenantId: string | null;
}

export const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * Read/write the active tenant.
 *
 * Throws if called outside a {@link TenantProvider} so misuse fails loudly
 * rather than silently returning a stale/empty tenant.
 */
export function useTenantContext(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenantContext must be called inside TenantProvider');
  }
  return ctx;
}

/** Shape of the `/api/me` fields the provider needs. Typed locally so the raw
 * bootstrap fetch (which runs before the RPC client/context exists) stays
 * `any`-free. */
interface MeResponse {
  tenants?: Array<{ id: string }>;
}

function readStoredTenantId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY);
  } catch {
    // localStorage can throw (private mode / disabled storage); degrade to null.
    return null;
  }
}

/**
 * Provider for the active-tenant context. Mounted at the SPA root (above the
 * router, sibling to ClerkProvider/QueryClientProvider) by Task 26/27.
 *
 * On mount it:
 *   1. seeds `activeTenantId` from localStorage,
 *   2. fetches `/api/me` (with the Clerk token) to learn the user's tenants,
 *   3. sets `defaultTenantId` when exactly one tenant exists, adopting it as
 *      active if nothing was stored.
 *
 * `setActiveTenantId` writes localStorage first (source of truth) then state.
 */
export function TenantProvider({ children }: { children: ReactNode }): ReactNode {
  const { getToken } = useAuth();
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(() =>
    readStoredTenantId(),
  );
  const [defaultTenantId, setDefaultTenantId] = useState<string | null>(null);

  // Stable identity (useCallback) so the mount effect below can depend on it
  // without re-running every render.
  const setActiveTenantId = useCallback((tenantId: string): void => {
    try {
      localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, tenantId);
    } catch {
      // Ignore storage failures; in-memory state below still switches tenant.
    }
    setActiveTenantIdState(tenantId);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadTenants = async (): Promise<void> => {
      const base = import.meta.env.VITE_API_BASE ?? '/';
      const url = `${base.replace(/\/$/, '')}/api/me`;
      const token = await getToken();
      const headers = new Headers();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      const stored = readStoredTenantId();
      if (stored) {
        headers.set('X-Tenant-Id', stored);
      }

      let res: Response;
      try {
        res = await fetch(url, { headers });
      } catch {
        return;
      }
      if (!res.ok || cancelled) {
        return;
      }

      const data = (await res.json()) as MeResponse;
      if (cancelled) {
        return;
      }

      const tenants = data.tenants ?? [];
      // `defaultTenantId` is the sole tenant when there's exactly one (used as a
      // display hint); null otherwise.
      setDefaultTenantId(tenants.length === 1 ? (tenants[0]?.id ?? null) : null);
      // Adopt the FIRST tenant as active whenever nothing is stored yet — for
      // ANY non-empty list, not just length===1. A user with several tenants
      // (e.g. a personal tenant + an org) would otherwise land with no active
      // tenant ("none selected"), no X-Tenant-Id header, and every /api/* call
      // failing tenant resolution. They can still switch via the TenantSwitcher.
      if (tenants.length > 0 && !readStoredTenantId()) {
        const first = tenants[0]?.id ?? null;
        if (first) {
          setActiveTenantId(first);
        }
      }
    };

    void loadTenants();
    return () => {
      cancelled = true;
    };
  }, [getToken, setActiveTenantId]);

  const value: TenantContextValue = {
    activeTenantId,
    setActiveTenantId,
    defaultTenantId,
  };

  // `auth.ts` (not `.tsx`) keeps this provider colocated with the context/hook,
  // so the element is built with `createElement` rather than JSX.
  return createElement(TenantContext.Provider, { value }, children);
}
